import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { apiClient } from '../api/client';
import { formatDateTime } from '../utils/format';

const REFRESH_MS = 4000;
const FETCH_LIMIT = 300;

const TYPE_LABELS = {
  2: { label: 'CALL', kind: 'call' },
  3: { label: 'RESULT', kind: 'result' },
  4: { label: 'ERROR', kind: 'error' },
};

const OcppTrace = () => {
  const { addToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [direction, setDirection] = useState('all');
  const [action, setAction] = useState('');
  const [chargerQuery, setChargerQuery] = useState(searchParams.get('charger') || '');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [exporting, setExporting] = useState(false);
  const initialLoadDone = useRef(false);

  const api = apiClient;

  const fetchEntries = useCallback(async () => {
    if (!initialLoadDone.current) setLoading(true);
    try {
      const params = { limit: FETCH_LIMIT };
      const charger = chargerQuery.trim();
      if (charger) params.charger_id = charger;
      if (action.trim()) params.action = action.trim();
      if (direction === 'in' || direction === 'out') params.direction = direction;
      const response = await api.get('/ocpp-trace', { params });
      const fresh = response.data.entries || [];
      setEntries(prev => (prev[0]?.id === fresh[0]?.id && prev.length === fresh.length ? prev : fresh));
      setLastUpdated(new Date());
    } catch (err) {
      if (!initialLoadDone.current) {
        addToast('Failed to fetch OCPP trace: ' + (err.response?.data?.detail || err.message), 'error');
        setEntries([]);
      }
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  }, [api, addToast, chargerQuery, action, direction]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      fetchEntries();
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh, fetchEntries]);

  const applyChargerFilter = (value) => {
    setChargerQuery(value);
    const next = new URLSearchParams(searchParams);
    if (value.trim()) next.set('charger', value.trim());
    else next.delete('charger');
    setSearchParams(next, { replace: true });
  };

  const typeBadge = (typeId) => {
    const meta = TYPE_LABELS[typeId] || { label: String(typeId ?? '—'), kind: 'neutral' };
    const colors = {
      call: { bg: '#cce5ff', fg: '#004085' },
      result: { bg: '#d4edda', fg: '#155724' },
      error: { bg: '#f8d7da', fg: '#721c24' },
      neutral: { bg: '#e2e3e5', fg: '#383d41' },
    };
    const c = colors[meta.kind];
    return (
      <span style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
        fontSize: '0.75rem', fontWeight: 700, backgroundColor: c.bg, color: c.fg,
      }}>
        {meta.label}
      </span>
    );
  };

  const dirBadge = (dir) => (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
      fontSize: '0.75rem', fontWeight: 700,
      backgroundColor: dir === 'in' ? '#e8f5e9' : '#fff3e0',
      color: dir === 'in' ? '#1b5e20' : '#e65100',
    }}>
      {dir === 'in' ? 'IN ← charger' : 'OUT → charger'}
    </span>
  );

  const exportJson = async () => {
    setExporting(true);
    try {
      const params = { limit: 1000 };
      const charger = chargerQuery.trim();
      if (charger) params.charger_id = charger;
      if (action.trim()) params.action = action.trim();
      if (direction === 'in' || direction === 'out') params.direction = direction;
      const response = await api.get('/ocpp-trace', { params });
      const rows = response.data.entries || [];
      const dump = {
        exported_at: new Date().toISOString(),
        filters: { charger_id: charger || null, action: action.trim() || null, direction },
        count: rows.length,
        entries: rows,
      };
      const blob = new Blob([JSON.stringify(dump, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ocpp-trace-${charger || 'all'}-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      addToast('Download failed: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setExporting(false);
    }
  };

  const payloadPreview = (entry) => {
    if (entry.error_code) return `${entry.error_code}: ${entry.error_description || ''}`;
    if (!entry.payload) return '';
    try {
      const text = JSON.stringify(entry.payload);
      return text.length > 140 ? `${text.slice(0, 140)}…` : text;
    } catch {
      return '';
    }
  };

  const styles = {
    container: { maxWidth: '1400px', margin: '0 auto', width: '100%', padding: '0 0.75rem', boxSizing: 'border-box' },
    header: { marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' },
    title: { fontSize: '1.75rem', fontWeight: 700, color: '#1a1a2e', margin: 0 },
    statusLine: { fontSize: '0.85rem', color: '#6c757d' },
    card: { backgroundColor: '#fff', borderRadius: '8px', padding: '1.25rem', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' },
    filterBar: { display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' },
    label: { fontSize: '0.8rem', fontWeight: 600, color: '#495057' },
    select: { padding: '0.45rem 0.6rem', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.9rem', backgroundColor: '#fff', minWidth: '140px' },
    btn: { padding: '0.45rem 1rem', backgroundColor: '#007bff', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 500 },
    btnSecondary: { padding: '0.45rem 1rem', backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 500 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
    th: { textAlign: 'left', padding: '0.55rem 0.65rem', borderBottom: '2px solid #dee2e6', color: '#495057', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' },
    td: { padding: '0.5rem 0.65rem', borderBottom: '1px solid #eee', color: '#333', verticalAlign: 'top' },
    pre: { margin: 0, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: '0.78rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#f8f9fa', padding: '0.75rem', borderRadius: '4px' },
    loadingText: { textAlign: 'center', padding: '2rem', color: '#6c757d' },
    emptyText: { textAlign: 'center', padding: '2rem', color: '#6c757d' },
    checkbox: { display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem', color: '#495057', cursor: 'pointer' },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>OCPP Message Trace</h1>
        <div style={styles.statusLine}>
          {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : '—'}
          {autoRefresh && ' · auto-refresh on'}
        </div>
      </div>

      <div style={styles.card}>
        <p style={{ marginTop: 0, color: '#495057', fontSize: '0.9rem' }}>
          Every frame sent to or received from a charger (CALL, CALLRESULT, CALLERROR), including MeterValues.
          Use this to see why live power is missing (look for MeterValues / Power.Active.Import).
        </p>
        <div style={styles.filterBar}>
          <label style={styles.label} htmlFor="traceCharger">Charger ID</label>
          <input
            id="traceCharger"
            type="search"
            value={chargerQuery}
            onChange={(e) => applyChargerFilter(e.target.value)}
            placeholder="e.g. FAST_001"
            style={{ ...styles.select, minWidth: '180px' }}
          />

          <label style={styles.label} htmlFor="traceDir">Direction</label>
          <select id="traceDir" value={direction} onChange={(e) => setDirection(e.target.value)} style={styles.select}>
            <option value="all">All</option>
            <option value="in">In (from charger)</option>
            <option value="out">Out (to charger)</option>
          </select>

          <label style={styles.label} htmlFor="traceAction">Action</label>
          <input
            id="traceAction"
            type="search"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            placeholder="MeterValues"
            style={{ ...styles.select, minWidth: '160px' }}
          />

          <label style={styles.checkbox}>
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            Auto-refresh (4s)
          </label>

          <button onClick={fetchEntries} style={styles.btn}>Refresh now</button>
          <button onClick={exportJson} style={styles.btnSecondary} disabled={exporting}>
            {exporting ? 'Preparing…' : 'Download JSON'}
          </button>
        </div>
      </div>

      <div style={styles.card}>
        {loading ? (
          <div style={styles.loadingText}>Loading OCPP messages…</div>
        ) : entries.length === 0 ? (
          <div style={styles.emptyText}>No OCPP messages recorded yet. Connect a charger or wait for traffic.</div>
        ) : (
          <div style={{ overflowX: 'auto', width: '100%' }}>
            <table style={{ ...styles.table, minWidth: '980px' }}>
              <thead>
                <tr>
                  <th style={styles.th}>Time</th>
                  <th style={styles.th}>Dir</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Action</th>
                  <th style={styles.th}>Charger</th>
                  <th style={styles.th}>Payload</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => {
                  const open = !!expanded[entry.id];
                  return (
                    <React.Fragment key={entry.id}>
                      <tr
                        onClick={() => setExpanded((prev) => ({ ...prev, [entry.id]: !prev[entry.id] }))}
                        style={{ cursor: 'pointer', backgroundColor: open ? '#f8fbff' : undefined }}
                      >
                        <td style={styles.td}>{formatDateTime(entry.created_at)}</td>
                        <td style={styles.td}>{dirBadge(entry.direction)}</td>
                        <td style={styles.td}>{typeBadge(entry.message_type_id)}</td>
                        <td style={{ ...styles.td, fontWeight: 600 }}>{entry.action || '—'}</td>
                        <td style={styles.td}>{entry.charger_id}</td>
                        <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '0.78rem', color: '#555' }}>
                          {payloadPreview(entry)}
                        </td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={6} style={{ ...styles.td, background: '#fafafa' }}>
                            <div style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '0.4rem' }}>
                              uniqueId: {entry.unique_id || '—'}
                            </div>
                            <pre style={styles.pre}>
                              {entry.raw || JSON.stringify(entry.payload, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default OcppTrace;
